// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Users, Activity, Shield, UserX, LogOut, RefreshCw, Database, Clock, CheckCircle, AlertCircle, Key } from 'lucide-react';

interface User {
  id: number;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_admin: boolean;
  is_banned: boolean;
  ban_reason?: string;
  banned_at?: string;
  subscription_tier: string;
  created_at: string;
  last_login?: string;
  // AI Agent Usage Tracking
  ai_agent_calls_today: number;
  ai_agent_calls_total: number;
  ai_agent_last_reset: string;
  ai_agent_daily_limit: number;
}

interface SystemStats {
  total_users: number;
  active_users: number;
  banned_users: number;
  total_coins: number;
  last_update?: string;
  total_analyses: number;
}

interface ContestSubmission {
  id: number;
  full_name: string;
  email: string;
  telegram_handle?: string;
  discord_handle?: string;
  review_text: string;
  word_count: number;
  video_url?: string;
  status: string;
  admin_notes?: string;
  submitted_at: string;
}

interface Contest {
  id: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  prize_first: number;
  prize_second: number;
  prize_third: number;
  status: string;
  rules_text?: string;
  min_word_count: number;
}

interface Notification {
  id: number;
  title: string;
  message: string;
  created_at: string;
}

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [contestSubmissions, setContestSubmissions] = useState<ContestSubmission[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'contest' | 'notifications' | 'data'>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchEmail, setSearchEmail] = useState('');

  // Notification states
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null);

  // Data management states
  const [populateStatus, setPopulateStatus] = useState<any>(null);
  const [isPopulating, setIsPopulating] = useState(false);
  const [populateMessage, setPopulateMessage] = useState<string | null>(null);
  const [tableStatus, setTableStatus] = useState<any>(null);

  // Contest management states
  const [contest, setContest] = useState<Contest | null>(null);
  const [isEditingContest, setIsEditingContest] = useState(false);
  const [contestForm, setContestForm] = useState<Partial<Contest>>({});
  const [contestUpdateMessage, setContestUpdateMessage] = useState<string | null>(null);

  // Data refresh states
  const [refreshStatus, setRefreshStatus] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [isRefreshingPairs, setIsRefreshingPairs] = useState(false);
  const [pairsRefreshMessage, setPairsRefreshMessage] = useState<string | null>(null);

  const { isDark} = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Helper function to format timestamps in user's local timezone
  const formatLocalTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    console.log('Auth token:', token ? `${token.substring(0, 20)}...` : 'NULL');
    if (!token) {
      setError('No authentication token found. Please login again.');
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (err: any) {
      console.error('Stats fetch error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch system statistics';
      setError(`Failed to fetch stats: ${errorMsg}`);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = searchEmail ? { email: searchEmail } : {};
      const response = await axios.get(`${API_BASE}/api/admin/users`, {
        headers: getAuthHeaders(),
        params
      });
      setUsers(response.data);
    } catch (err: any) {
      console.error('Users fetch error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch users';
      setError(`Failed to fetch users: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchContest = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/contest/1`, {
        headers: getAuthHeaders()
      });
      setContest(response.data);
      setContestForm(response.data);
    } catch (err: any) {
      console.error('Contest fetch error:', err.response?.data || err.message);
    }
  };

  const fetchContestSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/contest/1/registrations`, {
        headers: getAuthHeaders()
      });
      setContestSubmissions(response.data);
    } catch (err: any) {
      console.error('Contest registrations fetch error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch contest registrations';
      setError(`Failed to fetch contest registrations: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const updateContest = async () => {
    try {
      setLoading(true);
      setContestUpdateMessage(null);
      const response = await axios.put(`${API_BASE}/api/admin/contest/1`, contestForm, {
        headers: getAuthHeaders()
      });
      setContest(response.data.contest);
      setContestForm(response.data.contest);
      setIsEditingContest(false);
      setContestUpdateMessage('Contest updated successfully!');
      setTimeout(() => setContestUpdateMessage(null), 3000);
    } catch (err: any) {
      console.error('Contest update error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to update contest';
      setContestUpdateMessage(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefreshStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/refresh-status`, { headers: getAuthHeaders() });
      setRefreshStatus(response.data);
    } catch (err: any) {
      console.error('Refresh status fetch error:', err);
    }
  };

  const handleRefreshData = async () => {
    setRefreshMessage(null);
    setIsRefreshing(true);

    try {
      const response = await axios.post(`${API_BASE}/api/admin/refresh-explore-data`, {}, {
        headers: getAuthHeaders()
      });

      setRefreshMessage({
        type: 'success',
        text: response.data.message,
        details: `Refreshing ${response.data.scripts_count} data tables. This will take 3-10 minutes.`
      });

      // Fetch updated refresh status
      setTimeout(() => fetchRefreshStatus(), 1000);

    } catch (err: any) {
      console.error('Data refresh error:', err);
      const errorDetail = err.response?.data?.detail;

      if (err.response?.status === 429 && errorDetail) {
        // Cooldown period active
        setRefreshMessage({
          type: 'warning',
          text: errorDetail.message || errorDetail.error,
          details: errorDetail.next_available ? `Next refresh available: ${formatLocalTime(errorDetail.next_available)}` : ''
        });
      } else {
        setRefreshMessage({
          type: 'error',
          text: 'Failed to start data refresh',
          details: errorDetail || err.message
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshPairsAnalysis = async () => {
    setPairsRefreshMessage(null);
    setIsRefreshingPairs(true);

    try {
      const response = await axios.post(`${API_BASE}/api/admin/refresh-pairs-analysis`, {}, {
        headers: getAuthHeaders()
      });

      setPairsRefreshMessage({
        type: 'success',
        text: response.data.message,
        details: `Analyzed ${response.data.pairs_analyzed} pairs`
      });

    } catch (err: any) {
      console.error('Pairs refresh error:', err);
      setPairsRefreshMessage({
        type: 'error',
        text: 'Failed to refresh pairs analysis',
        details: err.response?.data?.detail || err.message
      });
    } finally {
      setIsRefreshingPairs(false);
    }
  };

  // Notification management functions
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/notifications`, {
        headers: getAuthHeaders()
      });
      setNotifications(response.data);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    }
  };

  const createNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      setError('Please fill in both title and message');
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/api/admin/notifications?title=${encodeURIComponent(notificationTitle)}&message=${encodeURIComponent(notificationMessage)}`,
        {},
        { headers: getAuthHeaders() }
      );

      setNotificationSuccess('Notification created successfully!');
      setNotificationTitle('');
      setNotificationMessage('');
      fetchNotifications();

      setTimeout(() => setNotificationSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to create notification:', err);
      setError('Failed to create notification');
    }
  };

  const deleteNotification = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/admin/notifications/${id}`, {
        headers: getAuthHeaders()
      });
      fetchNotifications();
    } catch (err: any) {
      console.error('Failed to delete notification:', err);
      setError('Failed to delete notification');
    }
  };

  // Data management functions
  const fetchTableStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/table-status`, {
        headers: getAuthHeaders()
      });
      setTableStatus(response.data);
    } catch (err: any) {
      console.error('Failed to fetch table status:', err);
      setError('Failed to load table status');
    }
  };

  const populateTables = async () => {
    if (!window.confirm('This will recompute all trading strategies data and take 7-15 minutes. Continue?')) {
      return;
    }

    setIsPopulating(true);
    setPopulateMessage('Starting batch scripts...');

    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/populate-tables`,
        {},
        {
          headers: getAuthHeaders(),
          timeout: 900000 // 15 minutes
        }
      );

      setPopulateStatus(response.data);
      setPopulateMessage(
        `✅ Completed ${response.data.successful}/${response.data.total_scripts} scripts in ${response.data.total_time_seconds}s`
      );

      // Refresh table status after populate
      setTimeout(() => fetchTableStatus(), 2000);
    } catch (err: any) {
      console.error('Failed to populate tables:', err);
      setPopulateMessage('❌ Failed to populate tables');
      setError(err.response?.data?.detail || 'Failed to populate tables');
    } finally {
      setIsPopulating(false);
    }
  };

  const quickRefreshCache = async () => {
    if (!window.confirm('This will clear the frontend cache and reload data instantly. Continue?')) {
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/api/admin/refresh-cache`,
        {},
        { headers: getAuthHeaders() }
      );

      setPopulateMessage('✅ Cache cleared successfully!');
      setTimeout(() => setPopulateMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to refresh cache:', err);
      setError('Failed to refresh cache');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRefreshStatus();

    // Auto-refresh status every 10 seconds when on overview tab
    const interval = setInterval(() => {
      if (activeTab === 'overview') {
        fetchRefreshStatus();
      }
    }, 10000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'contest') {
      fetchContest();
      fetchContestSubmissions();
    } else if (activeTab === 'notifications') {
      fetchNotifications();
    } else if (activeTab === 'data') {
      fetchTableStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchEmail]);

  const banUser = async (userId: number, reason: string) => {
    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/ban`,
        { reason },
        { headers: getAuthHeaders() }
      );
      fetchUsers();
      fetchStats(); // Refresh stats
    } catch (err) {
      setError('Failed to ban user');
    }
  };

  const unbanUser = async (userId: number) => {
    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/unban`, {}, { headers: getAuthHeaders() });
      fetchUsers();
      fetchStats(); // Refresh stats
    } catch (err) {
      setError('Failed to unban user');
    }
  };

  const resetUserPassword = async (userId: number, email: string) => {
    const newPassword = prompt(`Enter new password for ${email}:`);
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        { headers: getAuthHeaders() }
      );
      alert(`✅ Password reset successfully!\n\nEmail: ${email}\nNew Password: ${newPassword}\n\nShare this password securely with the user.`);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to reset password';
      setError(`Password reset failed: ${errorMsg}`);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>System Overview</h2>

      {stats && (
        <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className={`p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <h3 className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Total Users</h3>
                <p className="text-3xl font-bold text-blue-500">{stats.total_users}</p>
              </div>
            </div>
          </div>
          <div className={`p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-green-500" />
              <div>
                <h3 className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Active Users</h3>
                <p className="text-3xl font-bold text-green-500">{stats.active_users}</p>
              </div>
            </div>
          </div>
          <div className={`p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              <UserX className="h-8 w-8 text-red-500" />
              <div>
                <h3 className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Banned Users</h3>
                <p className="text-3xl font-bold text-red-500">{stats.banned_users}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Refresh Section */}
        <div className={`mt-6 p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Database className="h-5 w-5" />
                Explore Data Management
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                Refresh all data tables from CoinGecko Pro API
              </p>
            </div>
            <button
              onClick={handleRefreshData}
              disabled={isRefreshing || (refreshStatus?.currently_running !== null)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                isRefreshing || (refreshStatus?.currently_running !== null)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing || refreshStatus?.currently_running ? 'animate-spin' : ''}`} />
              {isRefreshing || refreshStatus?.currently_running ? 'Refreshing...' : 'Refresh All Data'}
            </button>
          </div>

          {/* Refresh Status */}
          {refreshStatus && (
            <div className="mt-4 space-y-4">
              {/* Currently Running Refresh */}
              {refreshStatus.currently_running && (
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-start gap-3">
                    <RefreshCw className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'} animate-spin flex-shrink-0 mt-0.5`} />
                    <div className="flex-1">
                      <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                        Refresh in Progress ({refreshStatus.currently_running.refresh_type})
                      </p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        Started: {formatLocalTime(refreshStatus.currently_running.started_at)}
                      </p>
                      {refreshStatus.currently_running.scripts_total > 0 && (
                        <div className={`text-sm mt-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          Progress: {refreshStatus.currently_running.scripts_success}/{refreshStatus.currently_running.scripts_total} scripts completed
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Last Successful Refresh */}
              {refreshStatus.last_successful_refresh && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800 border border-white/5' : 'bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <CheckCircle className={`h-5 w-5 text-green-500 flex-shrink-0 mt-0.5`} />
                    <div className="flex-1">
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Last Successful Refresh ({refreshStatus.last_successful_refresh.refresh_type})
                      </p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        Completed: {formatLocalTime(refreshStatus.last_successful_refresh.completed_at)}
                      </p>
                      <div className={`text-sm mt-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                        Scripts: {refreshStatus.last_successful_refresh.scripts_success}/{refreshStatus.last_successful_refresh.scripts_total} successful
                      </div>
                      {refreshStatus.last_successful_refresh.details && (
                        <details className="mt-2">
                          <summary className={`text-sm cursor-pointer ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                            View script details
                          </summary>
                          <div className={`mt-2 text-xs font-mono ${isDark ? 'text-white/50' : 'text-gray-500'} max-h-40 overflow-y-auto`}>
                            {refreshStatus.last_successful_refresh.details}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Automatic Refresh Info */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800 border border-white/5' : 'bg-gray-50'}`}>
                <div className="flex items-start gap-3">
                  <Clock className={`h-5 w-5 ${isDark ? 'text-white/60' : 'text-gray-600'} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1">
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Automatic Refresh Schedule
                    </p>
                    <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      {refreshStatus.automatic_refresh_enabled
                        ? `Enabled - Running every ${refreshStatus.automatic_refresh_interval_hours} hours`
                        : 'Disabled'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Refresh History */}
              {refreshStatus.recent_refreshes && refreshStatus.recent_refreshes.length > 0 && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800 border border-white/5' : 'bg-gray-50'}`}>
                  <p className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Recent Refresh History
                  </p>
                  <div className="space-y-2">
                    {refreshStatus.recent_refreshes.slice(0, 5).map((refresh: any, index: number) => (
                      <div key={index} className={`flex items-center justify-between text-sm p-2 rounded ${isDark ? 'bg-zinc-900/50' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          {refresh.status === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : refresh.status === 'failed' ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-blue-500" />
                          )}
                          <span className={`capitalize ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                            {refresh.refresh_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {refresh.status === 'success' && (
                            <span className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                              {refresh.scripts_success}/{refresh.scripts_total} scripts
                            </span>
                          )}
                          {refresh.status === 'failed' && refresh.error_message && (
                            <span className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'} max-w-xs truncate`} title={refresh.error_message}>
                              {refresh.error_message}
                            </span>
                          )}
                          <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                            {formatLocalTime(refresh.completed_at || refresh.started_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Refresh Message */}
          {refreshMessage && (
            <div className={`mt-4 p-4 rounded-lg border ${
              refreshMessage.type === 'success'
                ? isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'
                : refreshMessage.type === 'warning'
                ? isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'
                : isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {refreshMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    refreshMessage.type === 'warning' ? 'text-yellow-500' : 'text-red-500'
                  }`} />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${
                    refreshMessage.type === 'success'
                      ? 'text-green-700'
                      : refreshMessage.type === 'warning'
                      ? 'text-yellow-700'
                      : 'text-red-700'
                  }`}>
                    {refreshMessage.text}
                  </p>
                  {refreshMessage.details && (
                    <p className={`text-sm mt-1 ${
                      refreshMessage.type === 'success'
                        ? isDark ? 'text-green-400' : 'text-green-600'
                        : refreshMessage.type === 'warning'
                        ? isDark ? 'text-yellow-400' : 'text-yellow-600'
                        : isDark ? 'text-red-400' : 'text-red-600'
                    }`}>
                      {refreshMessage.details}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pairs Analysis Refresh */}
          <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-zinc-800/50 border border-white/5' : 'bg-blue-50/50 border border-blue-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Pairs Trading Analysis
                </h4>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  Refresh analysis for top 20 coins (190 pairs total)
                </p>
              </div>
              <button
                onClick={handleRefreshPairsAnalysis}
                disabled={isRefreshingPairs}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  isRefreshingPairs
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-500 hover:bg-purple-600 text-white shadow hover:shadow-lg'
                }`}
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshingPairs ? 'animate-spin' : ''}`} />
                {isRefreshingPairs ? 'Refreshing...' : 'Refresh Pairs'}
              </button>
            </div>

            {/* Pairs Refresh Message */}
            {pairsRefreshMessage && (
              <div className={`mt-3 p-3 rounded border ${
                pairsRefreshMessage.type === 'success'
                  ? isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'
                  : isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-2">
                  {pairsRefreshMessage.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      pairsRefreshMessage.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {pairsRefreshMessage.text}
                    </p>
                    {pairsRefreshMessage.details && (
                      <p className={`text-xs mt-1 ${
                        pairsRefreshMessage.type === 'success'
                          ? isDark ? 'text-green-400' : 'text-green-600'
                          : isDark ? 'text-red-400' : 'text-red-600'
                      }`}>
                        {pairsRefreshMessage.details}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tables Info */}
          <div className={`mt-4 grid grid-cols-2 gap-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            <div className={`p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <div className="font-medium mb-1">Bull Market Support Bands</div>
              <div className="text-xs opacity-75">USD & BTC</div>
            </div>
            <div className={`p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <div className="font-medium mb-1">Hedge Performers</div>
              <div className="text-xs opacity-75">BTC & ETH (All Timeframes)</div>
            </div>
            <div className={`p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <div className="font-medium mb-1">Price Comparisons</div>
              <div className="text-xs opacity-75">BTC & ETH (All Timeframes)</div>
            </div>
            <div className={`p-3 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
              <div className="font-medium mb-1">Automatic Scheduler</div>
              <div className="text-xs opacity-75">Every 4 hours (always enabled)</div>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>User Management</h2>

      <div className={`p-4 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
        <input
          type="text"
          placeholder="Search by email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className={`w-full px-3 py-2 rounded-md ${
            isDark
              ? 'bg-zinc-800 border-white/10 text-white placeholder-white/40'
              : 'bg-white border-gray-300 text-gray-900'
          } border`}
        />
      </div>

      <div className={`shadow overflow-hidden rounded-lg ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
        <ul className={`divide-y ${isDark ? 'divide-white/10' : 'divide-gray-200'}`}>
          {users.length === 0 ? (
            <li className="px-6 py-8 text-center">
              <p className={isDark ? 'text-white/50' : 'text-gray-500'}>
                {loading ? 'Loading users...' : 'No users found'}
              </p>
            </li>
          ) : (
            users.map((user) => (
              <li key={user.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.email}
                      </h3>
                      {user.is_admin && (
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-500/10 text-purple-500 border border-purple-500/20">
                          Admin
                        </span>
                      )}
                      {user.is_banned && (
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-red-500/10 text-red-500 border border-red-500/20">
                          Banned
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      {user.full_name && `${user.full_name} • `}
                      {user.subscription_tier}
                      {user.last_login && ` • Last login: ${new Date(user.last_login).toLocaleDateString()}`}
                    </p>
                    {user.ban_reason && (
                      <p className="text-sm text-red-500 mt-1">
                        Ban reason: {user.ban_reason}
                      </p>
                    )}
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'} mt-1`}>
                      Joined: {new Date(user.created_at).toLocaleDateString()}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'} mt-1 font-medium`}>
                      AI Agent Usage: {user.ai_agent_calls_today}/{user.ai_agent_daily_limit} today
                      {user.is_admin && ' (Unlimited)'}
                      {' • '}Total: {user.ai_agent_calls_total}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => resetUserPassword(user.id, user.email)}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-3 rounded text-sm transition-colors flex items-center gap-1"
                      title="Reset Password"
                    >
                      <Key className="h-4 w-4" />
                      Reset Password
                    </button>
                    {user.is_banned ? (
                      <button
                        onClick={() => unbanUser(user.id)}
                        className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded text-sm transition-colors"
                      >
                        Unban
                      </button>
                    ) : (
                      !user.is_admin && (
                        <button
                          onClick={() => {
                            const reason = prompt('Ban reason:');
                            if (reason) banUser(user.id, reason);
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded text-sm transition-colors"
                        >
                          Ban
                        </button>
                      )
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );

  const renderContestSubmissions = () => (
    <div className="space-y-6">
      <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Contest Management</h2>

      {/* Contest Settings Card */}
      {contest && (
        <div className={`p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Contest Settings
            </h3>
            {!isEditingContest ? (
              <button
                onClick={() => setIsEditingContest(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Edit Contest
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={updateContest}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingContest(false);
                    setContestForm(contest);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {contestUpdateMessage && (
            <div className={`mb-4 p-3 rounded ${contestUpdateMessage.includes('Error') ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
              {contestUpdateMessage}
            </div>
          )}

          {isEditingContest ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Contest Title
                </label>
                <input
                  type="text"
                  value={contestForm.title || ''}
                  onChange={(e) => setContestForm({...contestForm, title: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Status
                </label>
                <select
                  value={contestForm.status || ''}
                  onChange={(e) => setContestForm({...contestForm, status: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="ended">Ended</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={contestForm.start_date ? new Date(contestForm.start_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setContestForm({...contestForm, start_date: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={contestForm.end_date ? new Date(contestForm.end_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setContestForm({...contestForm, end_date: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  First Prize ($)
                </label>
                <input
                  type="number"
                  value={contestForm.prize_first || 0}
                  onChange={(e) => setContestForm({...contestForm, prize_first: parseFloat(e.target.value)})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Second Prize ($)
                </label>
                <input
                  type="number"
                  value={contestForm.prize_second || 0}
                  onChange={(e) => setContestForm({...contestForm, prize_second: parseFloat(e.target.value)})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Third Prize ($)
                </label>
                <input
                  type="number"
                  value={contestForm.prize_third || 0}
                  onChange={(e) => setContestForm({...contestForm, prize_third: parseFloat(e.target.value)})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  value={contestForm.description || ''}
                  onChange={(e) => setContestForm({...contestForm, description: e.target.value})}
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Title</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{contest.title}</p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Status</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} uppercase`}>{contest.status}</p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Start Date</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(contest.start_date).toLocaleString()}
                </p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>End Date</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(contest.end_date).toLocaleString()}
                </p>
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Prizes</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  1st: ${contest.prize_first} | 2nd: ${contest.prize_second} | 3rd: ${contest.prize_third}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Description</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{contest.description}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-8`}>Contest Registrations</h2>

      {loading ? (
        <div className="text-center py-12">
          <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Loading registrations...</p>
        </div>
      ) : contestSubmissions.length === 0 ? (
        <div className={`p-12 text-center rounded-lg ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white shadow'}`}>
          <p className={isDark ? 'text-white/50' : 'text-gray-500'}>No contest registrations yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {contestSubmissions.map((registration) => (
            <div
              key={registration.id}
              className={`p-6 rounded-lg shadow ${isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white'}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {registration.full_name}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {registration.email}
                  </p>
                  {registration.telegram_handle && (
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      Telegram: {registration.telegram_handle}
                    </p>
                  )}
                  {registration.discord_handle && (
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      Discord: {registration.discord_handle}
                    </p>
                  )}
                  {registration.usdt_trc20_address && (
                    <p className={`text-xs font-mono ${isDark ? 'text-emerald-400' : 'text-emerald-600'} mt-2`}>
                      💰 USDT (TRC20): {registration.usdt_trc20_address}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    registration.has_submission
                      ? registration.submission_status === 'winner'
                        ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                        : registration.submission_status === 'reviewed'
                        ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                        : 'bg-green-500/10 text-green-500 border border-green-500/20'
                      : 'bg-orange-500/10 text-orange-500 border border-orange-500/20'
                  }`}>
                    {registration.has_submission ? (registration.submission_status?.toUpperCase() || 'SUBMITTED') : 'REGISTERED'}
                  </span>
                  <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {registration.has_submission && registration.submitted_at
                      ? `Submitted: ${new Date(registration.submitted_at).toLocaleDateString()}`
                      : `Registered: ${new Date(registration.registered_at).toLocaleDateString()}`
                    }
                  </span>
                </div>
              </div>

              {registration.has_submission ? (
                <>
                  <div className={`mb-4 p-4 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Review</h4>
                      <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        {registration.word_count} words
                      </span>
                    </div>
                    <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      {registration.review_text}
                    </p>
                  </div>

                  {registration.video_url && (
                    <div className="mb-4">
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Video</h4>
                      <a
                        href={registration.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 text-sm break-all"
                      >
                        {registration.video_url}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <div className={`p-4 rounded ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    User registered but hasn't submitted a review yet
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className={`rounded-lg shadow ${isDark ? 'bg-zinc-900' : 'bg-white'} p-6`}>
        <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Create Notification
        </h2>

        {notificationSuccess && (
          <div className="bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded mb-4">
            {notificationSuccess}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
              Title
            </label>
            <input
              type="text"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="e.g., New Feature Released"
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark
                  ? 'bg-zinc-800 border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
              Message
            </label>
            <input
              type="text"
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="e.g., Check out our new trading strategies dashboard!"
              className={`w-full px-3 py-2 rounded-lg border ${
                isDark
                  ? 'bg-zinc-800 border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <button
            onClick={createNotification}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Create Notification
          </button>
        </div>
      </div>

      <div className={`rounded-lg shadow ${isDark ? 'bg-zinc-900' : 'bg-white'} p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            All Notifications
          </h2>
          <button
            onClick={fetchNotifications}
            className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors ${
              isDark
                ? 'bg-zinc-800 hover:bg-zinc-700 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
          >
            Refresh
          </button>
        </div>

        {notifications.length === 0 ? (
          <p className={`text-center py-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            No notifications yet
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    Title
                  </th>
                  <th className={`text-left py-3 px-4 font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    Message
                  </th>
                  <th className={`text-left py-3 px-4 font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    Created
                  </th>
                  <th className={`text-left py-3 px-4 font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr
                    key={notification.id}
                    className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                  >
                    <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <strong>{notification.title}</strong>
                    </td>
                    <td className={`py-3 px-4 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      {notification.message}
                    </td>
                    <td className={`py-3 px-4 ${isDark ? 'text-white/60' : 'text-gray-600'} text-sm`}>
                      {new Date(notification.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderDataManagement = () => (
    <div className="space-y-6">
      {/* Table Status */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-zinc-900' : 'bg-white'} p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Database Status
          </h2>
          <button
            onClick={fetchTableStatus}
            className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
              isDark
                ? 'bg-zinc-800 hover:bg-zinc-700 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            }`}
          >
            <RefreshCw className="h-4 w-4" />
            Check Status
          </button>
        </div>

        {tableStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(tableStatus.tables).map(([table, count]) => (
              <div
                key={table}
                className={`p-4 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-gray-50'}`}
              >
                <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {count as number}
                </div>
                <div className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {table.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-100'}`}>
      <div className={`shadow ${isDark ? 'bg-zinc-900 border-b border-white/10' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <Shield className={`h-8 w-8 ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`} />
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Admin Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-4">
            <nav className="flex space-x-4">
              {(['overview', 'users', 'contest', 'notifications', 'data'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`capitalize px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? isDark
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'bg-emerald-100 text-emerald-700'
                      : isDark
                        ? 'text-white/60 hover:text-white hover:bg-white/5'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
            <button
              onClick={handleLogout}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isDark
                  ? 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
                  : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
              }`}
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded mb-6 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold hover:text-red-600">×</button>
          </div>
        )}

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'contest' && renderContestSubmissions()}
        {activeTab === 'notifications' && renderNotifications()}
        {activeTab === 'data' && renderDataManagement()}
      </div>
    </div>
  );
};

export default AdminDashboard;
